import { ApolloError, gql } from '@apollo/client';
import client from '../apollo/client';
import { gqlTypes } from '../../types/gqlTypes';
import { CheckoutOffer } from './offer';

/**
 * Composed from generated *schema* types rather than a generated *operation* type, for the same
 * reason as the address query: `yarn gql:types` needs the web API on localhost:8080. The schema types
 * below are generated and committed, so this is fully typed with nothing hand-copied.
 */
type PurchaseResult = { purchaseConsumerStoreCart: gqlTypes.CartPurchaseResult };
type PurchaseVariables = { input: gqlTypes.ConsumerStoreCartInput };

const purchaseConsumerStoreCartMutation = gql`
  mutation nanoPurchaseConsumerStoreCart($input: ConsumerStoreCartInput!) {
    purchaseConsumerStoreCart(input: $input) {
      isReturningCustomer
      chargeInvoice {
        number
      }
    }
  }
`;

/**
 * The shopper-safe message the server attached to a purchase failure, if any.
 *
 * `purchaseConsumerStoreCart` surfaces recoverable states this way -- a declined card, an address
 * Recurly rejected, a coupon already redeemed on the account. Those are all things the shopper can act
 * on, and collapsing them into "something went wrong, please try again" invites an endless retry of
 * something that will never succeed.
 */
export function customerMessageFromError(error: unknown): string | undefined {
  if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
    const message = error.graphQLErrors[0].extensions?.customerMessage;
    return typeof message === 'string' && message ? message : undefined;
  }
  return undefined;
}

/**
 * Buys the one thing this flow sells: a Fi Mini with a 6-month membership.
 *
 * The SKUs come from the offer rather than from constants here, so this repo holds no copy of what web's
 * product config already owns.
 *
 * `shippingCode` is required in practice even though the schema marks it optional: the shop always sends
 * one, and an order placed without a shipping method has nothing telling fulfilment how to send the
 * hardware.
 *
 * The cart is a single item, which is why nano needs none of ecommerce's cart machinery -- no bundles,
 * gift cards, addons or multi-item mapping. `CartLineItemInput` is only `{ lineItemId, sku }` and the
 * server prices everything, so there is no client-side total to get wrong.
 *
 * No confirmation token is passed. The card was already stored as billing info by
 * `stripeCheckoutEndpoint`, which is the existing-user path and the only one nano uses; passing a
 * token inline here is the *new* account path and would be wrong.
 *
 * The GWA fields are what make a rescue alias resolve to its discount and what attributes the
 * redemption to the right rescue. `gwaToken` is required for the alias to resolve at all.
 */
export async function purchaseNanoUpsell(offer: CheckoutOffer, shippingCode: string | null) {
  const { data } = await client.mutate<PurchaseResult, PurchaseVariables>({
    mutation: purchaseConsumerStoreCartMutation,
    variables: {
      input: {
        cartItems: [
          {
            cartItemId: 'nano-mini-upsell',
            lineItem: { lineItemId: 'nano-mini-upsell-hardware', sku: offer.moduleSku },
            subscriptionLineItem: { lineItemId: 'nano-mini-upsell-membership', sku: offer.planSku },
            quantity: 1,
          },
        ],
        couponCode: offer.couponCode,
        shippingCode: shippingCode ?? undefined,
        gwaToken: offer.entitlement,
        gwaProgramId: offer.gwaProgramId,
        gwaSource: offer.gwaSource,
        currency: gqlTypes.Currency.USD,
        countryCode: 'US',
      },
    },
  });

  if (!data) {
    throw new Error('No data returned from purchase mutation');
  }
  return data.purchaseConsumerStoreCart;
}
